import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, Autocomplete, Divider, ToggleButton, ToggleButtonGroup, Grid, Stepper, Step, StepLabel, Menu, Snackbar, Alert } from '@mui/material';
import { Add, Delete, Edit, MoreVert, ContentCopy } from '@mui/icons-material';
import api from '../services/api';
import type { CreateRecipeRequest, Recipe, Material, Service, InsumoListItem } from '../types';
import TextField from '../components/common/TextField';

export const RecipesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Recipe | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [components, setComponents] = useState<any[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [insumos, setInsumos] = useState<InsumoListItem[]>([]);
  const [materialsMap, setMaterialsMap] = useState<Record<number, Material>>({});
  const [suppliers, setSuppliers] = useState<any[]>([]);
  // Proceso (operaciones + dependencias)
  const [ops, setOps] = useState<Array<{ id: number; name: string; serviceId?: number; durationHours: number; order?: number; dependsOn: number[] }>>([]);
  const [schedule, setSchedule] = useState<{ tasks: Array<{ id: number; name: string; durationDays: number; startDay: number; endDay: number; isCritical: boolean }>; totalDurationDays: number } | null>(null);
  const [generatingProcess, setGeneratingProcess] = useState(false);
  const [timeScale, setTimeScale] = useState<'days' | 'hours' | 'minutes'>('days');
  const [activeStep, setActiveStep] = useState(0);
  const [inputModes, setInputModes] = useState<Record<number, 'quantity' | 'yield'>>({});

  // Ellipsis menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRecipe, setMenuRecipe] = useState<Recipe | null>(null);
  // Confirm delete dialog
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Recipe | null>(null);
  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState<CreateRecipeRequest>({
    productId: 0,
    name: '',
    manufacturingTimeHours: 0,
    items: [],
  });

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(location.search);
        const pid = Number(params.get('productId') || 0);
        const r = await api.getRecipes({ page: 1, pageSize: 100, ...(pid ? { productId: pid } : {}) });
        setRecipes(r.recipes || []);
      } catch {}
      try {
        const p = await api.getProducts({ page: 1, pageSize: 500 });
        setProducts(p.products || []);
        setComponents(p.products || []);
      } catch {}
      try {
        const m = await api.getMaterials({ page: 1, pageSize: 500 });
        const mats = m.materials || [];
        setMaterials(mats);
        const mm: Record<number, Material> = {} as any;
        for (const it of mats) mm[it.id] = it as any;
        setMaterialsMap(mm);
      } catch {}
      try {
        const s = await api.getServices({ page: 1, pageSize: 500 });
        setServices(s.services || []);
      } catch {}
      try {
        const ins = await api.getInsumos({ page: 1, pageSize: 500 });
        setInsumos(ins.items || []);
      } catch {}
      try {
        const sps = await api.getSuppliers({ page: 1, pageSize: 500, search: '' } as any);
        setSuppliers(sps.suppliers || []);
      } catch {}
    })();
  }, []);

  // Autoabrir creación de receta si viene con query string (?new=1&productId=123)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openNew = params.get('new') === '1' || params.get('create') === '1';
    const pid = Number(params.get('productId') || 0);
    if (openNew && !open) {
      setEditing(null);
      setForm({ productId: pid || 0, name: '', manufacturingTimeHours: 0, items: [] });
      setOpen(true);
      // Limpia la query de navegación para evitar reapertura al volver
      navigate('/recipes', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, products.length]);

  // Autoabrir edición si viene con query string (?edit=ID)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = Number(params.get('edit') || 0);
    if (editId && !open) {
      (async () => {
        try {
          const rec = await api.getRecipe(editId);
          if (rec) handleOpen(rec as any);
        } catch (e) {
          console.error(e);
        } finally {
          // Limpia la query para evitar reapertura si se vuelve
          navigate('/recipes', { replace: true });
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleOpen = (rec?: Recipe) => {
    if (rec) {
      setEditing(rec);
      setForm({
        productId: rec.productId,
        name: rec.name,
        manufacturingTimeHours: rec.manufacturingTimeHours,
        items: rec.items.map(i => ({
          componentProductId: i.componentProductId,
          componentMaterialId: i.componentMaterialId,
          componentServiceId: (i as any).componentServiceId,
          quantity: i.quantity,
          unit: i.unit,
          supplierId: i.supplierId,
          procurementLeadDays: i.procurementLeadDays,
        } as any)),
      });
      // Configurar inputModes para items que tienen yieldQuantity
      const modes: Record<number, 'quantity' | 'yield'> = {};
      rec.items.forEach((item, idx) => {
        if (item.yieldQuantity && item.yieldQuantity > 0) {
          modes[idx] = 'yield';
        }
      });
      setInputModes(modes);
    } else {
      setEditing(null);
      setForm({ productId: 0, name: '', manufacturingTimeHours: 0, items: [] });
      setInputModes({});
    }
    setActiveStep(0);
    setOpen(true);
    // cargar operaciones si existe receta
    if (rec) {
      (async () => {
        try { const list = await api.getRecipeOperations(rec.id); setOps(list || []); } catch { setOps([]); }
        try { const sch = await api.getRecipeSchedule(rec.id); setSchedule({ tasks: sch.tasks || [], totalDurationDays: sch.totalDurationDays || 0 }); } catch { setSchedule(null); }
      })();
    } else {
      setOps([]); setSchedule(null);
    }
  };

  const getComponentUnit = (item: any): string => {
    if (item.componentMaterialId) {
      return materialsMap[item.componentMaterialId]?.unit || '';
    }
    if (item.componentServiceId) {
      return services.find(s => s.id === item.componentServiceId)?.unit || '';
    }
    if (item.componentProductId) {
      return 'u'; // Productos siempre en unidades
    }
    return '';
  };

  const handleSave = async () => {
    try {
      // La duración se determina por los servicios: persistimos ese valor
      const serviceHours = computeServiceHours(form as any);
      // Asegurar que cada item tenga la unidad correcta del componente y yieldQuantity si aplica
      const itemsWithCorrectUnit = form.items.map((item, idx) => {
        const isYieldMode = inputModes[idx] === 'yield';
        return {
          ...item,
          unit: getComponentUnit(item),
          // Si está en modo yield, calcular yieldQuantity = 1 / quantity
          yieldQuantity: isYieldMode && item.quantity > 0 ? 1 / item.quantity : undefined,
        };
      });
      const payload: CreateRecipeRequest = {
        ...form,
        items: itemsWithCorrectUnit,
        manufacturingTimeHours: serviceHours,
      } as any;
      if (editing) {
        const upd = await api.updateRecipe(editing.id, payload);
        setRecipes(prev => prev.map(r => (r.id === editing.id ? upd : r)));
      } else {
        const created = await api.createRecipe(payload);
        setRecipes(prev => [created, ...prev]);
        // persist process ops if any
        if ((ops || []).length > 0) { try { await api.upsertRecipeOperations(created.id, ops); } catch {} }
      }
      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDuplicate = async (recipe: Recipe) => {
    try {
      const payload: CreateRecipeRequest = {
        productId: recipe.productId,
        name: `${recipe.name} (copia)`,
        manufacturingTimeHours: recipe.manufacturingTimeHours || 0,
        items: (recipe.items || []).map(i => ({
          componentProductId: i.componentProductId,
          componentMaterialId: i.componentMaterialId,
          componentServiceId: (i as any).componentServiceId,
          componentInsumoId: (i as any).componentInsumoId,
          quantity: i.quantity,
          unit: i.unit,
          yieldQuantity: i.yieldQuantity,
        })),
      } as any;
      const created = await api.createRecipe(payload);
      setRecipes(prev => [created, ...prev]);
      setToast({ open: true, message: 'Formulación duplicada correctamente', severity: 'success' });
    } catch (e) {
      console.error(e);
      setToast({ open: true, message: 'Error al duplicar la formulación', severity: 'error' });
    }
  };

  const askDelete = (recipe: Recipe) => {
    setDeleteTarget(recipe);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteRecipe(deleteTarget.id);
      setRecipes(prev => prev.filter(r => r.id !== deleteTarget.id));
      setToast({ open: true, message: 'Formulación eliminada', severity: 'success' });
    } catch (e) {
      console.error(e);
      setToast({ open: true, message: 'Error al eliminar la formulación', severity: 'error' });
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, recipe: Recipe) => {
    setMenuAnchor(e.currentTarget);
    setMenuRecipe(recipe);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRecipe(null);
  };

  const productMap = useMemo(() => new Map(products.map((p: any) => [p.id, p])), [products]);

  // El listado crece rapido a medida que se cargan formulaciones (pedido de Micaela,
  // 29/09): buscar por nombre de producto o de la formulacion. El "cliente" (NN, TWP,
  // O1, etc.) no es un campo propio, va como sufijo del nombre del producto, asi que
  // este mismo buscador lo cubre. Mismo criterio que el buscador de Materias Primas:
  // sin mayusculas/tildes, cada palabra tiene que aparecer en el texto.
  const [recipeSearch, setRecipeSearch] = useState('');
  const filteredRecipes = useMemo(() => {
    const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const terms = norm(recipeSearch).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return recipes;
    return recipes.filter(r => {
      const productName = r.productName || productMap.get(r.productId)?.name || '';
      const hay = norm(`${productName} ${r.name || ''}`);
      return terms.every(t => hay.includes(t));
    });
  }, [recipes, recipeSearch, productMap]);
  const sortedProducts = useMemo(() => ([...products] as any[]).sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || '')), [products]);
  const sortedMaterials = useMemo(() => ([...materials] as any[]).sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || '')), [materials]);
  const sortedServices = useMemo(() => ([...services] as any[]).sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || '')), [services]);
  const sortedSuppliers = useMemo(() => ([...suppliers] as any[]).sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || '')), [suppliers]);

  // Helpers para cálculo de costo unitario, horas de servicio, unidades fabricables y lead total
  const computeServiceHours = (rec: { items: any[] }) => {
    let serviceHours = 0;
    for (const it of (rec.items || [])) {
      const svcId = (it as any).componentServiceId as number | undefined;
      if (svcId) {
        const sv = services.find(s => s.id === svcId);
        if (sv) serviceHours += (Number(it.quantity) || 0) * (Number(sv.processingHoursPerUnit) || 0);
      }
    }
    return Number.isFinite(serviceHours) ? Number(serviceHours.toFixed(2)) : 0;
  };

  const computeUnitCost = (rec: Recipe) => {
    let cost = 0;
    for (const it of (rec.items || [])) {
      if (it.componentMaterialId && materialsMap[it.componentMaterialId]) {
        const m = materialsMap[it.componentMaterialId];
        cost += (Number(it.quantity) || 0) * (Number(m.cost) || 0);
      } else if (it.componentProductId && productMap.get(it.componentProductId)) {
        const p: any = productMap.get(it.componentProductId);
        cost += (Number(it.quantity) || 0) * (Number(p.cost) || 0);
      } else if ((it as any).componentServiceId) {
        const sv = services.find(s => s.id === (it as any).componentServiceId);
        if (sv) cost += (Number(it.quantity) || 0) * (Number(sv.cost) || 0);
      }
    }
    return Number.isFinite(cost) ? Number(cost.toFixed(2)) : 0;
  };

  const computeBuildable = (rec: Recipe) => {
    const materialItems = (rec.items || []).filter(i => !!i.componentMaterialId);
    if (materialItems.length === 0) return 0;
    let buildable = Infinity;
    for (const it of materialItems) {
      const mat = it.componentMaterialId ? materialsMap[it.componentMaterialId] : undefined;
      if (!mat) continue;
      const have = Number(mat.currentStock) || 0;
      const req = Math.max(1e-6, Number(it.quantity) || 0);
      buildable = Math.min(buildable, Math.floor(have / req));
    }
    return buildable === Infinity ? 0 : buildable;
  };

  const supplierMap = useMemo(() => new Map((suppliers || []).map((s: any) => [s.id, s])), [suppliers]);

  const resolveProcurementLead = (it: any) => {
    if (it?.procurementLeadDays != null) return Number(it.procurementLeadDays) || 0;
    const material = it?.componentMaterialId ? materialsMap[it.componentMaterialId] : undefined;
    const supplierId = it?.supplierId || material?.primarySupplierId;
    if (supplierId) {
      const supplier = supplierMap.get(supplierId);
      if (supplier) return Number(supplier.leadTimeDays ?? supplier.LeadTimeDays ?? 0) || 0;
    }
    return 0;
  };

  const computeLeadDays = (rec: Recipe) => {
    // Lead de compra: gating factor = máximo entre materiales (por item)
    const materialsLead = (rec.items || [])
      .filter(i => !!i.componentMaterialId)
      .reduce((mx, i) => Math.max(mx, resolveProcurementLead(i)), 0);
    // Duración de servicios en horas => días de fabricación (aprox.)
    const serviceHours = computeServiceHours(rec);
    const manufacturingDays = Math.ceil(serviceHours / 8);
    return materialsLead + manufacturingDays;
  };

  const timeScaleConfig = useMemo(() => {
    switch (timeScale) {
      case 'hours':
        return { label: 'horas', short: 'h', multiplier: 24, decimalDigits: 2 };
      case 'minutes':
        return { label: 'minutos', short: 'min', multiplier: 24 * 60, decimalDigits: 0 };
      default:
        return { label: 'días', short: 'd', multiplier: 1, decimalDigits: 2 };
    }
  }, [timeScale]);

  const formatDurationForScale = (daysValue: number, digits?: number) => {
    const value = daysValue * timeScaleConfig.multiplier;
    const precision = typeof digits === 'number' ? digits : timeScaleConfig.decimalDigits;
    return `${value.toFixed(precision)} ${timeScaleConfig.label}`;
  };

  // Simple yield preview per material line
  const computeYieldForItem = (it: any): { yieldPerPurchaseUnit?: number; label?: string } => {
    const matId = it?.componentMaterialId as number | undefined;
    if (!matId) return {};
    const m = materialsMap[matId];
    if (!m) return {};
    const content = Number(m.contentPerPurchaseUnit ?? 0);
    if (!content || !Number.isFinite(content)) return {};
    const qty = Math.max(0, Number(it.quantity) || 0);
    const wastePct = Math.max(0, Number(m.defaultWastePct ?? 0)); // 0-100
    const consumoReal = qty * (1 + (wastePct / 100));
    if (consumoReal <= 0) return {};
    const y = content / consumoReal;
    if (!Number.isFinite(y) || y <= 0) return {};
    const label = `Rinde aprox: ${y.toFixed(2)} u por ${m.purchaseUnitName || 'unidad de compra'}`;
    return { yieldPerPurchaseUnit: y, label };
  };

  const costBreakdown = useMemo(() => {
    let materialTotal = 0;
    let serviceTotal = 0;
    let productTotal = 0;
    for (const it of form.items || []) {
      const qty = Number(it.quantity) || 0;
      if (it.componentMaterialId && materialsMap[it.componentMaterialId]) {
        materialTotal += qty * (Number(materialsMap[it.componentMaterialId].cost) || 0);
      } else if (it.componentProductId && productMap.get(it.componentProductId)) {
        productTotal += qty * (Number((productMap.get(it.componentProductId) as any)?.cost) || 0);
      } else if ((it as any).componentServiceId) {
        const sv = services.find(s => s.id === (it as any).componentServiceId);
        serviceTotal += qty * (Number(sv?.cost) || 0);
      }
    }
    return {
      materials: Number(materialTotal.toFixed(2)),
      services: Number(serviceTotal.toFixed(2)),
      products: Number(productTotal.toFixed(2)),
    };
  }, [form.items, materialsMap, productMap, services]);

  const totalCostPreview = Number((costBreakdown.materials + costBreakdown.services + costBreakdown.products).toFixed(2));
  const serviceHoursPreview = computeServiceHours(form as any);
  const leadPreview = computeLeadDays({ ...(editing as any) || {}, items: form.items, manufacturingTimeHours: serviceHoursPreview } as any);
  const hasServiceComponents = (form.items || []).some((i: any) => i.componentServiceId);
  const canGoNext = (form.productId || 0) > 0 && (form.name || '').trim().length > 0 && (form.items?.length || 0) > 0;

  const buildSchedulePreview = () => {
    const serviceItems = (form.items || []).filter((i: any) => i.componentServiceId);
    if (!serviceItems.length) return null;
    let cursorDays = 0;
    const tasks = serviceItems.map((it: any, idx: number) => {
      const svc = services.find(s => s.id === it.componentServiceId);
      const hoursPerUnit = Number(svc?.processingHoursPerUnit) || 0;
      const qty = Number(it.quantity) || 0;
      const durationHours = Math.max(0, hoursPerUnit * qty);
      const durationDays = durationHours / 24;
      const startDay = cursorDays;
      const endDay = startDay + durationDays;
      cursorDays = endDay;
      return {
        id: idx + 1,
        name: svc?.name ? `Servicio: ${svc.name}` : `Servicio ${it.componentServiceId}`,
        startDay,
        endDay,
        durationDays: Math.max(durationDays, 0),
        isCritical: true,
      };
    });
    return { tasks, totalDurationDays: cursorDays };
  };

  const generateProcessFromServices = async () => {
    const preview = buildSchedulePreview();
    if (preview) setSchedule(preview);
    if (!editing) return;
    const serviceItems = (form.items || []).filter((i: any) => i.componentServiceId);
    const nextOps = serviceItems.map((it: any, idx: number) => {
      const svc = services.find(s => s.id === it.componentServiceId);
      const hoursPerUnit = Number(svc?.processingHoursPerUnit) || 0;
      const qty = Number(it.quantity) || 0;
      const durationHours = Math.max(0, hoursPerUnit * qty);
      return {
        id: idx + 1,
        name: svc?.name ? `Servicio: ${svc.name}` : `Servicio ${it.componentServiceId}`,
        serviceId: it.componentServiceId,
        durationHours,
        order: idx + 1,
        dependsOn: [] as number[],
      };
    });
    setGeneratingProcess(true);
    try {
      await api.upsertRecipeOperations((editing as any).id, nextOps);
      const sch = await api.getRecipeSchedule((editing as any).id);
      setOps(nextOps);
      setSchedule({ tasks: sch.tasks || [], totalDurationDays: sch.totalDurationDays || 0 });
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingProcess(false);
    }
  };

  useEffect(() => {
    const preview = buildSchedulePreview();
    if (preview) {
      setSchedule(preview);
    } else {
      setSchedule(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.items), services.map(s => s.id).join(',')]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Formulaciones de fabricación</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Nueva formulación</Button>
      </Stack>

      <TextField
        placeholder="Buscar por producto o cliente (nombre)…"
        value={recipeSearch}
        onChange={e => setRecipeSearch(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      />

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tiempo (hs)</TableCell>
                  <TableCell align="right">Costo unit.</TableCell>
                  <TableCell align="right">Fabricable</TableCell>
                  <TableCell align="right">Tiempo total (días)</TableCell>
                  <TableCell>Insumos</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecipes.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.productName || productMap.get(r.productId)?.name || r.productId}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{computeServiceHours(r)}</TableCell>
                    <TableCell align="right">${computeUnitCost(r).toLocaleString()}</TableCell>
                    <TableCell align="right">{computeBuildable(r)}</TableCell>
                    <TableCell align="right">{computeLeadDays(r)}</TableCell>
                    <TableCell>{r.items?.map(i => `${i.componentMaterialName || i.componentServiceName || i.componentProductName || productMap.get(i.componentProductId as any)?.name || i.componentMaterialId || (i as any).componentServiceId || i.componentProductId} x${i.quantity}${i.unit ? ' ' + i.unit : ''}`).join(', ')}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => openMenu(e, r)}><MoreVert /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {recipes.length === 0 && (
                  <TableRow><TableCell colSpan={8}><Typography color="text.secondary">No hay formulaciones aún.</Typography></TableCell></TableRow>
                )}
                {recipes.length > 0 && filteredRecipes.length === 0 && (
                  <TableRow><TableCell colSpan={8}><Typography color="text.secondary">Sin resultados para "{recipeSearch}".</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { width: '90vw', height: '90vh' } }}
      >
        <DialogTitle>{editing ? 'Editar formulación' : 'Nueva formulación'}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            <Step><StepLabel>Datos de entrada</StepLabel></Step>
            <Step><StepLabel>Resumen y proceso</StepLabel></Step>
          </Stepper>

          {activeStep === 0 && (
            <Stack spacing={3} sx={{ mt: 0 }}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={600}>Qué fabrico y a qué costo/tiempo</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Autocomplete
                          fullWidth
                          options={sortedProducts}
                          getOptionLabel={(p: any) => p ? `${p.name}${p.sku ? ` (${p.sku})` : ''}` : ''}
                          isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                          value={sortedProducts.find((p: any) => p.id === form.productId) || null}
                          onChange={(_, val: any | null) => setForm({ ...form, productId: val?.id || 0 })}
                          renderInput={(params) => (
                            <TextField {...params} label="Producto" placeholder="Buscar producto..." fullWidth />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Nombre de la formulación"
                          placeholder="Ej: Formulación creatina en polvo"
                          value={form.name || ''}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="body2" color="text.secondary">
                      Esta formulación está expresada por unidad terminada: cada cantidad corresponde a lo requerido para fabricar 1 unidad del producto final.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
                    <Typography variant="subtitle1" fontWeight={600}>Insumos (materias primas, servicios o subproductos)</Typography>
                    <Button size="medium" variant="contained" startIcon={<Add />} onClick={() => {
                      const next = [...form.items];
                      if (materials.length > 0) {
                        next.push({ componentMaterialId: materials[0].id, quantity: 1, unit: materials[0].unit || '' } as any);
                      } else if (services.length > 0) {
                        next.push({ componentServiceId: services[0].id, quantity: 1, unit: services[0].unit || '' } as any);
                      } else if (components.length > 0) {
                        next.push({ componentProductId: components[0].id, quantity: 1 } as any);
                      } else {
                        next.push({ quantity: 1 } as any);
                      }
                      setForm({ ...form, items: next });
                    }}>Agregar insumo</Button>
                  </Stack>
                  <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 920 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Insumo</TableCell>
                          <TableCell align="right">Cantidad</TableCell>
                          <TableCell align="right">Unidad</TableCell>
                          <TableCell>Proveedor</TableCell>
                          <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Typography color="text.secondary">Sin insumos cargados todavía.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {form.items.map((it, idx) => {
                          const showSupplier = Boolean(it.componentMaterialId);
                          return (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ minWidth: 140 }}>
                                <TextField select fullWidth label="Tipo" value={(it as any).componentInsumoId ? 'insumo' : it.componentServiceId ? 'servicio' : it.componentMaterialId ? 'material' : 'producto'} onChange={e => {
                                  const type = e.target.value as string;
                                  const items = [...form.items];
                                  if (type === 'material') {
                                    if (materials.length === 0) { window.alert('No hay materias primas cargadas'); return; }
                                    items[idx] = { ...items[idx], componentMaterialId: materials[0].id, componentProductId: undefined, componentServiceId: undefined, componentInsumoId: undefined, unit: materials[0].unit || (items[idx] as any).unit } as any;
                                  } else if (type === 'servicio') {
                                    if (services.length === 0) { window.alert('No hay servicios cargados'); return; }
                                    items[idx] = {
                                      ...items[idx],
                                      componentServiceId: services[0].id,
                                      componentProductId: undefined,
                                      componentMaterialId: undefined,
                                      componentInsumoId: undefined,
                                      unit: services[0].unit || (items[idx] as any).unit,
                                      quantity: 1
                                    } as any;
                                  } else if (type === 'insumo') {
                                    if (insumos.length === 0) { window.alert('No hay insumos cargados'); return; }
                                    items[idx] = { ...items[idx], componentInsumoId: insumos[0].id, componentProductId: undefined, componentMaterialId: undefined, componentServiceId: undefined, unit: insumos[0].unit || (items[idx] as any).unit } as any;
                                  } else {
                                    if (components.length === 0) { window.alert('No hay productos disponibles'); return; }
                                    items[idx] = { ...items[idx], componentProductId: components[0].id, componentMaterialId: undefined, componentServiceId: undefined, componentInsumoId: undefined } as any;
                                  }
                                  setForm({ ...form, items });
                                }}>
                                  <MenuItem value="material">Materia prima</MenuItem>
                                  <MenuItem value="servicio">Servicio</MenuItem>
                                  <MenuItem value="insumo">Insumo</MenuItem>
                                  <MenuItem value="producto">Producto</MenuItem>
                                </TextField>
                              </TableCell>
                              <TableCell sx={{ minWidth: 220 }}>
                                {it.componentMaterialId ? (
                                  <Autocomplete
                                    fullWidth
                                    options={sortedMaterials as any}
                                    getOptionLabel={(m: any) => m ? `${m.code ? `[${m.code}] ` : ''}${m.name}${m.unit ? ` (${m.unit})` : ''}` : ''}
                                    isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                                    value={(sortedMaterials as any).find((m: any) => m.id === it.componentMaterialId) || null}
                                    onChange={(_, v: any | null) => {
                                      const items = [...form.items];
                                      if (v) items[idx] = { ...items[idx], componentMaterialId: v.id, componentProductId: undefined, componentServiceId: undefined, unit: v.unit || (items[idx] as any).unit } as any;
                                      else items[idx] = { ...items[idx], componentMaterialId: undefined } as any;
                                      setForm({ ...form, items });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Materia prima" fullWidth />}
                                  />
                                ) : it.componentServiceId ? (
                                  <Autocomplete
                                    fullWidth
                                    options={sortedServices as any}
                                    getOptionLabel={(s: any) => s ? `${s.name}${s.unit ? ` (${s.unit})` : ''}` : ''}
                                    isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                                    value={(sortedServices as any).find((s: any) => s.id === it.componentServiceId) || null}
                                    onChange={(_, v: any | null) => {
                                      const items = [...form.items];
                                      if (v) {
                                        items[idx] = {
                                          ...items[idx],
                                          componentServiceId: v.id,
                                          componentProductId: undefined,
                                          componentMaterialId: undefined,
                                          unit: v.unit || (items[idx] as any).unit,
                                          quantity: items[idx].quantity || 1
                                        } as any;
                                      } else items[idx] = { ...items[idx], componentServiceId: undefined } as any;
                                      setForm({ ...form, items });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Servicio" fullWidth />}
                                  />
                                ) : (it as any).componentInsumoId ? (
                                  <Autocomplete
                                    fullWidth
                                    options={insumos}
                                    getOptionLabel={(ins: any) => ins ? `${ins.name}${ins.code ? ` (${ins.code})` : ''}` : ''}
                                    isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                                    value={insumos.find((ins) => ins.id === (it as any).componentInsumoId) || null}
                                    onChange={(_, v: any | null) => {
                                      const items = [...form.items];
                                      if (v) items[idx] = { ...items[idx], componentInsumoId: v.id, componentProductId: undefined, componentMaterialId: undefined, componentServiceId: undefined, unit: v.unit || (items[idx] as any).unit } as any;
                                      else items[idx] = { ...items[idx], componentInsumoId: undefined } as any;
                                      setForm({ ...form, items });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Insumo" fullWidth />}
                                  />
                                ) : (
                                  <Autocomplete
                                    fullWidth
                                    options={sortedProducts as any}
                                    getOptionLabel={(p: any) => p ? `${p.name}${p.sku ? ` (${p.sku})` : ''}` : ''}
                                    isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                                    value={(sortedProducts as any).find((p: any) => p.id === it.componentProductId) || null}
                                    onChange={(_, v: any | null) => {
                                      const items = [...form.items];
                                      if (v) items[idx] = { ...items[idx], componentProductId: v.id, componentMaterialId: undefined, componentServiceId: undefined, componentInsumoId: undefined } as any;
                                      else items[idx] = { ...items[idx], componentProductId: undefined } as any;
                                      setForm({ ...form, items });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Producto" fullWidth />}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ width: 200 }}>
                                {it.componentServiceId ? (
                                  <Typography color="text.secondary" variant="body2">Calculado</Typography>
                                ) : (
                                  <Stack spacing={0.5}>
                                    <ToggleButtonGroup
                                      size="small"
                                      value={inputModes[idx] || 'quantity'}
                                      exclusive
                                      onChange={(_, v) => v && setInputModes({ ...inputModes, [idx]: v })}
                                      sx={{ '& .MuiToggleButton-root': { fontSize: '0.65rem', py: 0.25, px: 0.5 } }}
                                    >
                                      <ToggleButton value="quantity">Consumo</ToggleButton>
                                      <ToggleButton value="yield">Rinde</ToggleButton>
                                    </ToggleButtonGroup>
                                    <TextField
                                      type="number"
                                      size="small"
                                      label={inputModes[idx] === 'yield'
                                        ? `1 ${materialsMap[it.componentMaterialId as number]?.unit || 'u'} rinde...`
                                        : `Cantidad (${materialsMap[it.componentMaterialId as number]?.unit || 'u'})`}
                                      value={
                                        inputModes[idx] === 'yield' && it.quantity > 0
                                          ? (1 / it.quantity).toFixed(2)
                                          : it.quantity
                                      }
                                      onChange={e => {
                                        const v = Number(e.target.value);
                                        const items = [...form.items];
                                        if (inputModes[idx] === 'yield') {
                                          // Validate: yield must be positive
                                          if (v <= 0) return;
                                          items[idx] = { ...items[idx], quantity: 1 / v };
                                        } else {
                                          items[idx] = { ...items[idx], quantity: v };
                                        }
                                        setForm({ ...form, items });
                                      }}
                                      helperText={
                                        inputModes[idx] === 'yield'
                                          ? `1 ${materialsMap[it.componentMaterialId as number]?.unit || 'u'} = ${
                                              it.quantity > 0 ? (1 / it.quantity).toFixed(1) : '?'
                                            } productos (consumo: ${it.quantity > 0 ? it.quantity.toFixed(4) : '?'} por producto)`
                                          : it.componentMaterialId
                                          ? (computeYieldForItem(it).label || `Consumo por producto: ${it.quantity} ${materialsMap[it.componentMaterialId as number]?.unit || 'u'}`)
                                          : ''
                                      }
                                      fullWidth
                                      inputProps={{ step: inputModes[idx] === 'yield' ? 0.5 : 0.0001, min: 0.0001 }}
                                    />
                                  </Stack>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ width: 140 }}>
                                <Typography color="text.secondary" variant="body2">
                                  {it.componentMaterialId
                                    ? (materialsMap[it.componentMaterialId]?.unit || '—')
                                    : it.componentServiceId
                                      ? (services.find(s => s.id === it.componentServiceId)?.unit || '—')
                                      : (productMap.get(it.componentProductId as number) as any)?.unit || 'u'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ minWidth: 180 }}>
                                {showSupplier ? (
                                  <Autocomplete
                                    fullWidth
                                    options={sortedSuppliers as any}
                                    getOptionLabel={(s: any) => s ? s.name : ''}
                                    isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                                    value={(sortedSuppliers as any).find((s: any) => s.id === (it as any).supplierId) || null}
                                    onChange={(_, v: any | null, reason) => {
                                      if (reason === 'clear' || v !== null) {
                                        const items=[...form.items];
                                        items[idx] = { ...items[idx], supplierId: v?.id };
                                        setForm({ ...form, items });
                                      }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Proveedor" fullWidth />}
                                  />
                                ) : (
                                  <Typography color="text.secondary" variant="body2">No aplica</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton color="error" onClick={() => { const items = form.items.filter((_, i) => i !== idx); setForm({ ...form, items }); }}>
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Los servicios (corte, confección, etiquetado) cargalos como insumos de tipo Servicio, con su costo unitario. Si no querés que limiten la capacidad ni el stock, no los cargues como ítems de inventario. Para contemplar mermas, ajustá la cantidad por unidad (ej.: 350g con 2% de pérdida = 0.357 kg). El lead de compra se obtiene del proveedor asignado (o del principal de la materia prima).
                  </Typography>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Costo unitario estimado</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>${totalCostPreview.toFixed(2)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Lead time total de la formulación</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>{leadPreview} {leadPreview === 1 ? 'día' : 'días'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Tiempo de producción (hs)</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>{serviceHoursPreview.toFixed(2)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          )}

          {activeStep === 1 && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>Costos (solo lectura)</Typography>
                  {form.items.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>Cargá insumos en el paso anterior para ver los costos.</Typography>
                  ) : (
                    <>
                      <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Insumo</TableCell>
                              <TableCell align="right">Cantidad</TableCell>
                              <TableCell align="right">Costo unit.</TableCell>
                              <TableCell align="right">Subtotal</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {form.items.map((it, i) => {
                              let name = '';
                              let unitCost = 0;
                              if (it.componentMaterialId && materialsMap[it.componentMaterialId]) {
                                const m = materialsMap[it.componentMaterialId]; name = m.name; unitCost = Number(m.cost) || 0;
                              } else if (it.componentProductId && productMap.get(it.componentProductId)) {
                                const p: any = productMap.get(it.componentProductId); name = p.name; unitCost = Number(p.cost) || 0;
                              } else if ((it as any).componentServiceId) {
                                const sv = services.find(s => s.id === (it as any).componentServiceId); name = sv?.name || 'Servicio'; unitCost = Number(sv?.cost) || 0;
                              } else {
                                name = '—';
                              }
                              const qty = Number(it.quantity) || 0;
                              const sub = qty * unitCost;
                              return (
                                <TableRow key={i}>
                                  <TableCell>{name}{it.unit ? ` (${it.unit})` : ''}</TableCell>
                                  <TableCell align="right">{qty}</TableCell>
                                  <TableCell align="right">${unitCost.toFixed(2)}</TableCell>
                                  <TableCell align="right">${sub.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow>
                              <TableCell colSpan={3} align="right"><strong>Total</strong></TableCell>
                              <TableCell align="right">${totalCostPreview.toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Stack spacing={1.5} sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">Total materias primas: <strong>${costBreakdown.materials.toFixed(2)}</strong></Typography>
                        <Typography variant="body2" color="text.secondary">Total servicios: <strong>${costBreakdown.services.toFixed(2)}</strong></Typography>
                        {costBreakdown.products > 0 && (
                          <Typography variant="body2" color="text.secondary">Total subproductos: <strong>${costBreakdown.products.toFixed(2)}</strong></Typography>
                        )}
                      </Stack>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Typography variant="body2" color="text.secondary">Lead time total estimado: <strong>{leadPreview} {leadPreview === 1 ? 'día' : 'días'}</strong></Typography>
                        <Typography variant="body2" color="text.secondary">Unidades fabricables con stock actual: <strong>{computeBuildable({ ...(editing as any) || {}, items: form.items } as any)}</strong></Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        El cálculo de unidades fabricables toma sólo las materias primas de la formulación (por unidad) y compara contra el stock disponible.
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>Tiempo y proceso (Gantt)</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={timeScale}
                        onChange={(_, val) => { if (val) setTimeScale(val); }}
                      >
                        <ToggleButton value="days">Días</ToggleButton>
                        <ToggleButton value="hours">Horas</ToggleButton>
                        <ToggleButton value="minutes">Minutos</ToggleButton>
                      </ToggleButtonGroup>
                      <Button variant="outlined" size="small" onClick={generateProcessFromServices} disabled={!editing || generatingProcess || !hasServiceComponents}>
                        {generatingProcess ? 'Generando...' : 'Generar proceso automático desde insumos'}
                      </Button>
                    </Stack>
                  </Stack>
                  {!hasServiceComponents && (
                    <Typography color="text.secondary">Agregá insumos de tipo Servicio para calcular duración y cronograma.</Typography>
                  )}
                  {hasServiceComponents && !schedule && (
                    <Typography color="text.secondary">Cargá cantidades y servicios para ver el cronograma.</Typography>
                  )}
                  {schedule && (
                    <Box sx={{ mt: 1, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>Cronograma de la formulación (Total: {formatDurationForScale(schedule.totalDurationDays || 0)})</Typography>
                        {(() => {
                          const tasks = schedule?.tasks || [];
                          const pxPerDay = 48;
                          const rowH = 26;
                          const maxEnd = Math.max(0, ...tasks.map(t => (t.endDay ?? (t.startDay + t.durationDays))));
                          const width = Math.max(1, maxEnd) * pxPerDay;
                          const totalUnits = Math.ceil(maxEnd * timeScaleConfig.multiplier);
                          const tickTarget = 30;
                          const tickStep = Math.max(1, Math.ceil(totalUnits / tickTarget));
                          const axisUnits: number[] = [];
                          for (let unit = 0; unit <= totalUnits; unit += tickStep) axisUnits.push(unit);
                          if (axisUnits.length === 0 || axisUnits[axisUnits.length - 1] !== totalUnits) axisUnits.push(totalUnits);
                          return (
                            <Box sx={{ overflowX: 'auto' }}>
                              {/* Eje de tiempo */}
                              <Box sx={{ position: 'relative', height: 24, minWidth: width }}>
                                {axisUnits.map((unit, idx) => {
                                  const left = (unit / timeScaleConfig.multiplier) * pxPerDay;
                                  return <Box key={`axis-${idx}`} sx={{ position: 'absolute', left, top: 0, width: 1, height: '100%', bgcolor: 'divider' }} />;
                                })}
                                {axisUnits.map((unit, idx) => {
                                  const left = (unit / timeScaleConfig.multiplier) * pxPerDay;
                                  return (
                                    <Typography key={`lbl-${idx}`} variant="caption" sx={{ position: 'absolute', left: left + 4, top: 2, color: 'text.secondary' }}>
                                      {`${unit}${timeScaleConfig.short}`}
                                    </Typography>
                                  );
                                })}
                              </Box>
                              {/* Tareas */}
                              <Box sx={{ position: 'relative', minWidth: width, height: Math.max(1, tasks.length) * rowH, borderTop: '1px solid', borderColor: 'divider' }}>
                                {axisUnits.map((unit, idx) => {
                                  const left = (unit / timeScaleConfig.multiplier) * pxPerDay;
                                  return <Box key={`grid-${idx}`} sx={{ position: 'absolute', left, top: 0, width: 1, height: '100%', bgcolor: 'divider', opacity: 0.25 }} />;
                                })}
                                {tasks.map((t, i) => {
                                  const left = (t.startDay || 0) * pxPerDay;
                                  const w = Math.max(6, (t.durationDays || 0) * pxPerDay);
                                  const isPurchase = String(t.name || '').startsWith('Compra:');
                                  const color = t.isCritical ? 'error.main' : (isPurchase ? 'warning.main' : 'primary.main');
                                  const textColor = t.isCritical ? '#fff' : (isPurchase ? '#111' : '#fff');
                                  const startLabel = formatDurationForScale(t.startDay || 0);
                                  const endLabel = formatDurationForScale(t.endDay || ((t.startDay || 0) + (t.durationDays || 0)));
                                  const durationLabel = formatDurationForScale(t.durationDays || 0);
                                  return (
                                    <Tooltip key={t.id} title={`${t.name} — inicio ${startLabel}, fin ${endLabel} (${durationLabel})`}>
                                      <Box sx={{ position: 'absolute', left, top: i * rowH + 3, width: w, height: rowH - 6, bgcolor: color, opacity: 0.95, borderRadius: 1, boxShadow: 1, border: '1px solid', borderColor: 'rgba(0,0,0,0.08)' }}>
                                        <Typography variant="caption" sx={{ color: textColor, px: 0.5, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', display: 'block' }}>{isPurchase ? `Compra: ${t.name.replace(/^Compra:\s*/i, '')}` : t.name}</Typography>
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                              {/* Leyenda */}
                              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 12, height: 12, bgcolor: 'primary.main', borderRadius: 0.5 }} /> <Typography variant="caption">Producción</Typography></Stack>
                                <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: 0.5 }} /> <Typography variant="caption">Compra</Typography></Stack>
                                <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: 0.5 }} /> <Typography variant="caption">Tarea crítica</Typography></Stack>
                              </Stack>
                            </Box>
                          );
                        })()}
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Costo unitario estimado</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>${totalCostPreview.toFixed(2)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Lead time total de la formulación</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>{leadPreview} {leadPreview === 1 ? 'día' : 'días'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Tiempo de producción (hs)</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>{serviceHoursPreview.toFixed(2)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          {activeStep > 0 && <Button onClick={() => setActiveStep(activeStep - 1)}>Volver</Button>}
          {activeStep === 0 ? (
            <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!canGoNext}>Siguiente</Button>
          ) : (
            <Button variant="contained" onClick={handleSave}>{editing ? 'Guardar' : 'Crear'}</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Ellipsis menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { if (menuRecipe) handleOpen(menuRecipe); closeMenu(); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { if (menuRecipe) handleDuplicate(menuRecipe); closeMenu(); }}>
          <ContentCopy fontSize="small" sx={{ mr: 1 }} /> Duplicar
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { if (menuRecipe) askDelete(menuRecipe); closeMenu(); }} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Eliminar
        </MenuItem>
      </Menu>

      {/* Confirm delete dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => { setConfirmDeleteOpen(false); setDeleteTarget(null); }}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que querés eliminar la formulación <strong>{deleteTarget?.name}</strong>?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDeleteOpen(false); setDeleteTarget(null); }}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};
